import { AsyncPipe } from "@angular/common";
import { filter, map } from "rxjs";
import { Component, HostListener, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faChalkboard,
  faChalkboardTeacher,
  faCircleQuestion,
  faDoorOpen,
  faSearch,
  faUser,
} from "@fortawesome/pro-light-svg-icons";
import {
  NgbCollapseModule,
  NgbDropdownModule,
  NgbModal,
  NgbOffcanvasModule,
} from "@ng-bootstrap/ng-bootstrap";
import { injectLocalStorage } from "ngxtension/inject-local-storage";
import { ClassService } from "src/app/features/classes/services/class.service";
import { OrgService } from "src/app/features/orgs/services/org.service";
import { JaxStateService } from "src/app/core/jax/services/jax-state.service";
import { SuggestionService } from "src/app/core/jax/services/suggestion.service";
import { ProfileService } from "src/app/core/profile/profile.service";
import { SearchModalComponent } from "src/app/core/search-box/search-modal/search-modal.component";
import { BreadcrumbComponent } from "src/app/core/ui/breadcrumb/breadcrumb.component";
import { PendingNotificationsComponent } from "src/app/features/orgs/pending-notifications/pending-notifications.component";
import { CreditBalanceComponent } from "src/app/core/ui/credit-balance/credit-balance.component";
import { AssumeBannerComponent } from "src/app/core/layout/assume-banner/assume-banner.component";
import { AssumeSessionService } from "src/app/core/services/assume-session.service";
import { User } from "src/app/subscription/services/auth/auth.interface";
import { AuthService } from "src/app/subscription/services/auth/auth.service";
import { SubscriptionService } from "src/app/subscription/services/subscription/subscription.service";
import { environment } from "src/environments/environment";
import {
  AdminIcon,
  AIIcon,
  SuperIcon,
  ArtifactsIcon,
  BibleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DashboardIcon,
  ExitIcon,
  FilesIcon,
  LearningIcon,
  MenuBarsIcon,
  MenuExitIcon,
  PlannerIcon,
  SafetyIcon,
  SettingsIcon,
  StandardsIcon,
  TeamsIcon,
  UsageIcon,
} from "../../ui/icons";

@Component({
  selector: "app-main-layout",
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    FontAwesomeModule,
    NgbCollapseModule,
    NgbDropdownModule,
    NgbOffcanvasModule,
    FormsModule,
    AsyncPipe,
    BreadcrumbComponent,
    PendingNotificationsComponent,
    CreditBalanceComponent,
    AssumeBannerComponent,
  ],
  templateUrl: "./main-layout.component.html",
  styleUrl: "./main-layout.component.scss",
})
export class MainLayoutComponent {
  isNavbarCollapsed = true;
  isNavbarMinimized = false;

  // Inject services declaratively
  private authService = inject(AuthService);
  private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);
  private profileService = inject(ProfileService);
  private modalService = inject(NgbModal);
  private classService = inject(ClassService);
  private siteService = inject(OrgService);
  jaxState = inject(JaxStateService);
  private suggestionService = inject(SuggestionService);
  protected assumeSession = inject(AssumeSessionService);

  // Sidebar sections whose deepest URL we restore when clicked again.
  // Leaf-page sections (usage, safety, settings, artifacts, classroom) are excluded.
  private readonly trackedSections = [
    "documents",
    "classes",
    "planner",
    "standards",
    "bible",
    "admin",
    "super",
  ] as const;

  // Keyed by user id — localStorage is shared across tabs and accounts on
  // the same browser, so a flat map leaked one account's deep links into
  // the next session. Each account only ever sees its own paths.
  private lastSectionPaths = injectLocalStorage<Record<string, Record<string, string>>>(
    "sidebar:lastPathsByUser",
    { defaultValue: {} },
  );

  private currentUser = toSignal(this.authService.$user, { initialValue: null });

  /**
   * Where the section's nav item points. Normally the deepest URL the user
   * visited in that section; when they're ALREADY at that URL, the section
   * root — so clicking the active nav item again "drops down" to the
   * listing (e.g. /classes/{id}/announcements → /classes).
   */
  sidebarLink(section: (typeof this.trackedSections)[number]): string {
    const root = `/${section}`;
    const userId = this.currentUser()?.id;
    const saved = userId ? this.lastSectionPaths()[userId]?.[section] : null;
    if (!saved) return root;
    return this.currentPrimaryPath() === saved ? root : saved;
  }

  private currentPrimaryPath(): string {
    return this.router.url.split("?")[0].split("#")[0].split("(")[0];
  }

  constructor() {
    // Process `?jax=...` on every navigation that carries it. Lives here
    // (not in AppComponent) so the Jax/help/suggestion service graph is only
    // instantiated when the authenticated app shell is mounted — public org
    // pages don't have a Jax assistant outlet and shouldn't pay for it.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.checkForJaxQuery(event.url);
        this.recordSectionPath(event.urlAfterRedirects);
      });
  }

  private recordSectionPath(url: string): void {
    const userId = this.currentUser()?.id;
    if (!userId) return;
    const primary = url.split("?")[0].split("#")[0].split("(")[0];
    const segments = primary.split("/").filter(Boolean);
    if (segments.length === 0) return;
    const section = segments[0];
    if (!this.trackedSections.includes(section as never)) return;
    this.lastSectionPaths.update((all) => ({
      ...all,
      [userId]: { ...all[userId], [section]: primary },
    }));
  }

  private checkForJaxQuery(url: string): void {
    try {
      const urlObj = new URL(url, window.location.origin);
      const jaxQuery = urlObj.searchParams.get('jax');
      if (!jaxQuery) return;

      // Queue the prompt. SuggestionService is a BehaviorSubject — the
      // assistant component receives the latest value when it mounts.
      // Pass openJax=false so the service doesn't fire its own navigation;
      // we open the outlet and strip the query param in a single navigate
      // call below to avoid the second nav cancelling the first.
      this.suggestionService.populateInput(jaxQuery, false, true);

      this.router.navigate(
        [{ outlets: { jax: ['assistant'] } }],
        {
          queryParams: { jax: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        },
      );
    } catch (error) {
      console.warn('Failed to process Jax query parameter:', error);
    }
  }

  // Declarative observables for template consumption
  user$ = this.authService.$user;
  hasEnrollments$ = this.classService.hasEnrollments();
  hasTeams$ = this.siteService.getAllMyCampuses().pipe(map((teams) => teams.length > 0));
  /**
   * True when the user is an admin of at least one org OR an admin of at
   * least one campus. Reads from the user payload that's already loaded
   * (auth.$user.orgs / .campuses) — no extra HTTP call. Either role is
   * sufficient to surface the /admin section in the sidebar.
   */
  isOrgAdmin$ = this.authService.$user.pipe(
    map((user) => {
      if (!user) return false;
      // Collaborators (external org-admin grants) have admin access too —
      // distinct role label, same /admin section. Only ACTIVE rows count;
      // a pending collaborator grant (status 'invited') confers nothing until
      // accepted. See docs/external-org-admin.md.
      const orgAdmin = user.orgs?.some(
        (o) => o.status === 'active' && (o.role === 'admin' || o.role === 'collaborator'),
      ) ?? false;
      const campusAdmin = user.campuses?.some((c) => c.role === 'admin') ?? false;
      return orgAdmin || campusAdmin;
    }),
  );

  // Check environment flag for which assistant to use
  get useJaxAssistantV2(): boolean {
    return environment.USE_JAX_ASSISTANT_V2;
  }

  icons = {
    classroomIcon: faChalkboard,
    plannerIcon: PlannerIcon,
    classesIcon: faChalkboardTeacher,
    filesIcon: FilesIcon,
    artifactsIcon: ArtifactsIcon,
    standardsIcon: StandardsIcon,
    bibleIcon: BibleIcon,
    learningIcon: LearningIcon,
    settingsIcon: SettingsIcon,
    profileIcon: faUser,
    teamsIcon: TeamsIcon,
    chevronRight: ChevronRightIcon,
    chevronLeft: ChevronLeftIcon,
    menuBarsIcon: MenuBarsIcon,
    menuExitIcon: MenuExitIcon,
    exitIcon: ExitIcon,
    aiIcon: AIIcon,
    helpIcon: faCircleQuestion,
    dashboardIcon: DashboardIcon,
    usageIcon: UsageIcon,
    safetyIcon: SafetyIcon,
    searchIcon: faSearch,
    studentViewIcon: faDoorOpen,
    adminIcon: AdminIcon,
    superIcon: SuperIcon,
  };

  exitIcon = ExitIcon;
  private searchModalOpen = false;

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Cmd+K (Mac) or Ctrl+K (Windows/Linux) to open search
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.openSearch();
    }
  }

  openSearch() {
    if (this.searchModalOpen) return;
    this.searchModalOpen = true;

    const modalRef = this.modalService.open(SearchModalComponent, {
      size: 'lg',
      windowClass: 'main-modal search-modal-window',
      backdropClass: 'search-modal-backdrop',
    });

    modalRef.result.finally(() => {
      this.searchModalOpen = false;
    });
  }

  collapseNav() {
    this.isNavbarCollapsed = true;
  }

  toggleAiAssistant() {
    this.jaxState.toggle();
  }

  signout() {
    this.authService.signout().subscribe();
  }

  changePassword() {
    this.authService.openChangePasswordModal();
  }

  openSettings() {
    this.profileService.openSettingsModal();
  }

  manageSubscription(user: User) {
    // No personal plan → send them to subscribe. Otherwise they hold a real
    // Stripe sub (starter/pro/max, monthly or annual) → open the portal.
    // Gate on the entitlement, not the bare "starter" string — paying Starter
    // customers have subscribed = "starter_monthly"/"starter_annual", never
    // the bare "starter" fallback.
    if (!user.entitlement?.has_personal) {
      this.router.navigate(["/subscribe"]);
    } else {
      this.subscriptionService.redirectToCustomerPortal().subscribe();
    }
  }

  subscriptionDescription(user: User) {
    return "Manage Subscription";
  }


  openProfileModal() {
    this.profileService.openProfileModal().then(
      (result) => {
        // Handle successful profile update if needed
        console.log("Profile updated:", result);
      },
      () => {
        // Handle modal dismissal if needed
        console.log("Profile modal dismissed");
      },
    );
  }

}
